import { IUserInfo } from '@umu/umu-types';
import { getLangSwitchList } from '@umu/umu-lang-switch';

/**
 * @file 帐号相关接口
 * @author wayou
 *
 * API: https://docs.google.com/document/d/1dCpSNzhQJQ8mkiZRA8_XhqS6PyAUeUp9L5qBNo-1OUk/edit#heading=h.8thxy4xnpv8o
 * API: https://docs.google.com/document/d/1Qygf2GtArhaS3ZEJMidXsbGqYxPJeHWu8v1qXjw0K_E/edit
 */

import { $window } from '@umu/umu-dom';
import { localStorageHelper, STORAGE_KEY } from '@umu/umu-storage';
import { find, getData } from '@umu/umu-util';
import { isInMiniProgram } from '@umu/umu-ua';
import { reportError } from 'common/src/services/errorLogger';
import { fetch } from '@umu/umu-fetch';
import { getModuleData } from 'common/src/services';
import CaptchaManager from './captchaManager/captchaManager';
import { TENCENT_CAPTCHA_ERROR_CODE } from './captchaManager/enum';

// 在这里重新导出的目的是为了在其他地方使用的时候，可以方便地使用，不用去 import captchaManager，屏蔽 captchaManager
export const CAPTCHA_ERROR_CODE = {
    ...TENCENT_CAPTCHA_ERROR_CODE,
};

export const CAPTCHA_ACTION = {
    FORGET_PWD: 'forget_pwd',
    REGISTER: 'register',
    QUICK_REGISTER: 'quick_register',
    MFA: 'mfa',
    BIND_PHONE: 'bind_phone',
    WX_BIND: 'wx_bind',
};

export interface ICaptchaData {
    action: string;
    containerId: string;
}

export interface IAPIResponse {
    data: any;
    status: boolean;
    error: string;
}

// 手机号绑定
// https://docs.google.com/document/d/1mNWtj9AFzyaXG_K13jsYWdmxi4wpxrhQAGYqDJUcmDo/edit#
interface IBindPhoneData {
    area_code: string;
    phone: string;
    code: string;
    captcha?: string;
    tk?: string;
}

async function bindPhone(data: IBindPhoneData) {
    const post = (args: IBindPhoneData) => fetch.post(`/passport/ajax/account/bindPhone`, args);
    const isMini = await isInMiniProgram();
    if (isMini) {
        return post({ ...data, tk: 'mp' });
    }

    data.tk = 'mp';
    return post(data);
}

interface IRegisterSubmitData {
    area_code: string;
    phone: string;
    passwd: string;
    check_code: string;
    bl_param?: string;
    captcha?: string;
    tk?: string;
}
async function registerSubmit(data: IRegisterSubmitData) {
    const post = (args: IRegisterSubmitData) => fetch.post(`/passport/ajax/account/registerstepone`, args);
    const isMini = await isInMiniProgram();
    if (isMini) {
        return post({ ...data, tk: 'mp' });
    }

    data.tk = 'mp';
    return post(data);
}

// 检查邮箱
/**
 *
 * @param {object} data
 * @param {string} data.email
 * @param {string} data.check_code
 *
 * @returns
 */
function checkEmail(data: { email: string; check_code: string }) {
    return fetch.post('/passport/ajax/account/checkemail', data);
}

interface IRregisterQuickSubmitData {
    name: string;
    email?: string;
    phone?: string;
    bl_param?: string;
    captcha?: string;
    tk?: string;
}
async function registerQuickSubmit(data: IRregisterQuickSubmitData) {
    const post = (args: IRregisterQuickSubmitData) => fetch.post(`/passport/ajax/account/loginquick`, args);
    const isMini = await isInMiniProgram();
    if (isMini) {
        return post({ ...data, tk: 'mp' });
    }
    data.tk = 'mp';

    return post(data);
}
interface IGetVerifyCodeData {
    'area_code': string;
    'phone': string;
    'data[data_type]': number; // 8、注册,9、找回密码
    'type': number; // 1:数字，2:语音
    'captcha'?: string;
    'tk'?: string;
    'captcha_v2'?: string;
}

async function getVerifyCode(data: IGetVerifyCodeData) {
    const post = (args: IGetVerifyCodeData) => fetch.post(`/passport/ajax/account/sendsms`, args);
    const isMini = await isInMiniProgram();
    if (isMini) {
        return post({ ...data, tk: 'mp' });
    }
    data.tk = 'mp';
    return post(data);
}

function sendEmailVerifyCode(data: { email: string; type: number }) {
    const url = '/uapi/v1/user/send-verify-email';
    return new Promise<[{ error_message: string }] | [null, IAPIResponse]>((resolve) => {
        fetch.post(url, data).then(
            (result: any) => {
                resolve([null, result]);
            },
            (result: any) => {
                resolve([result]);
            },
        );
    });
}

function verifyCode(data: { 'data[data_type]': number; 'area_code': string; 'phone': string; 'check_code': string }) {
    return fetch.post('/passport/ajax/account/checkphone', data);
}

function loadCategoryData() {
    return fetch.get('/passport/ajax/account/getroletree');
}

function loadAddrs() {
    return fetch.get('/passport/ajax/account/citylist');
}

function submitProfile(data: { name: string; company_name: string; province: string; city: string; phone: string }) {
    return fetch.post('/passport/ajax/account/registersteptwo', data);
}

function submitRoleSelect(data: { step: number; identity: string }) {
    return fetch.post('/passport/ajax/account/updaterole', data);
}

function login(data: { area_code?: string; username: string; passwd: string }) {
    return fetch.post('/passport/ajax/account/login', data);
}
/* 密码修改格式错误信息返回字段处理
 * @return 是否修改成功
        100: lang('登录密码不能包含非法字符'),
        101: lang('企业管理员修改了密码安全设置，请设置新登录密码'),
        102: lang('企业管理员修改了密码安全设置，请设置新登录密码'),
        103: lang('您的企业设置了密码有效期，当前密码已过期，请设置新登录密码'),
        104: lang('您所在的企业要求首次登录时修改密码，请设置新登录密码。'),
        105: lang('您所在的企业要求重置密码后首次登录时修改密码，请设置新登录密码。'),
 */
function getModifyPwdStatus(data: { code?: number }) {
    let result = false;
    const codeArr = [101, 102, 103, 104, 105];
    const code = data.code || -1;
    result = codeArr.indexOf(code) > -1;
    return result;
}
function ssoLogin(data: { name: string; redirect_url: string }) {
    return fetch.get('/api/enterpriseauthlogin/getauthurl', data);
}

function resetPswd(data: { passwd: string; token: string }) {
    return fetch.post('/passport/ajax/account/passwordreset', data, { silent: true });
}

interface IVerifyMailData {
    email: string;
    captcha?: string;
    tk?: string;
}
async function verifyMail(data: IVerifyMailData) {
    const post = (args: IVerifyMailData) => fetch.post(`/passport/ajax/account/addforgotemail`, args);
    const isMini = await isInMiniProgram();
    if (isMini) {
        return post({ ...data, tk: 'mp' });
    }

    data.tk = 'mp';
    return post(data);
}

function checkAccount(data: { account: string; type: string }) {
    return fetch.post('/passport/ajax/account/isregistered', data);
}

/**
 * 退出登录
 */
function logout() {
    return fetch.get('/passport/account/logout');
}

async function getVerifyCodeByCaptcha(
    data: IGetVerifyCodeData,
    { action, containerId }: { action: string; containerId: string },
    sendSmsFunc: (data: IGetVerifyCodeData) => Promise<IAPIResponse>,
) {
    const captchaManager = new CaptchaManager();

    const captcha_v2 = await captchaManager.getCaptchaInfo(action, containerId);
    const reqData = Object.assign({}, data, { captcha_v2 });
    let getVerifyCodeResult = await sendSmsFunc(reqData);
    captchaManager.reset();

    // 如果验证码错误，就不进行了，这个时候再进行都没用了，这个错误一般是 js 加载错误
    if (getVerifyCodeResult.data && getVerifyCodeResult.data.need_recaptcha && !captchaManager.getIsErrorCaptcha()) {
        const captcha_v2 = await captchaManager.getCaptchaInfo(action, containerId, true);
        const reqData = Object.assign({}, data, { captcha_v2 });
        getVerifyCodeResult = await sendSmsFunc(reqData);
        captchaManager.reset();

        // 人机验证失败，直接抛出错误
        if (getVerifyCodeResult.data && getVerifyCodeResult.data.need_recaptcha) {
            reportError({
                error: 'Verification code error',
                componentName: 'AccountService',
                additionalInfo: { desc: 'Captcha verification failed after retry', result: getVerifyCodeResult },
            });
            throw getVerifyCodeResult;
        }
    } else if (getVerifyCodeResult.data && getVerifyCodeResult.data.need_recaptcha) {
        reportError({
            error: 'Verification code error',
            componentName: 'AccountService',
            additionalInfo: { desc: 'Captcha verification failed', result: getVerifyCodeResult },
        });
        throw getVerifyCodeResult;
    }

    return getVerifyCodeResult;
}

export const accountDataService = {
    registerSubmit,
    registerQuickSubmit,
    getVerifyCode: (data: IGetVerifyCodeData, captchaData: ICaptchaData) => {
        return getVerifyCodeByCaptcha(data, captchaData, getVerifyCode);
    },
    verifyCode,
    loadCategoryData,
    loadAddrs,
    submitProfile,
    submitRoleSelect,
    login,
    ssoLogin,
    resetPswd,
    verifyMail,
    checkAccount,
    logout,
    bindPhone,
    getModifyPwdStatus,
    sendEmailVerifyCode,
    checkEmail,
};

export const REGISTER_BY = {
    PHONE: 'PHONE',
    MAIL: 'MAIL',
};

// 验证用户名，用户名支持 5-32个字母、数字 和 "."，"_"，"-" 三种字符
// https://pro.modao.cc/app/xtRBMhhPJursN0x1R4n0L4cKHZRpwCv#screen=sc714bc8a3fd90d86a92c62
const loginNameReg = /^(?![0-9]+$)(?![\.\-_]+$)(?![a-zA-Z]+$)[0-9A-Za-z\.\-_]{5,32}$/i;
export function validateLoginName(loginName: string) {
    return loginNameReg.test(loginName);
}

export function validatePhone(data: string) {
    return /^[0-9]+$/.test(data);
}

export function validateMail(data: string) {
    return /\S+@\S+/.test(data);
}

export function validatePswd(data: string) {
    return !!(data && data.length > 5);
}
export function validateNewRulePswd(data: string) {
    if (!data) {
        return false;
    }
    return data.length > 7 && data.length < 17 && /([0-9]+.*[a-zA-Z]+)|([a-zA-Z]+.*[0-9]+)/.test(data);
}

// -------------------------------------------------------------
interface ILangSwitchListItem {
    key: string;
    value: string;
}

export const langSwitchList = getLangSwitchList();

export function getCurrentLang() {
    const current = getModuleData('Lang');
    return current;
}

export function getCurrentLangValue() {
    const current = getCurrentLang();
    const currentLangObj = find(langSwitchList, (obj: ILangSwitchListItem) => {
        return obj.key === current;
    });
    return currentLangObj && currentLangObj.value;
}

/**
 * 设置系统语言偏好
 * @param {string}    lang     zh-cn 等
 * @param {boolean}   needSave 是否要请求接口保存到用户信息中
 * @param {Function}  callback 设置完成后的回调函数
 */
export function setSiteLang(lang: string, needSave?: boolean, callback?: () => void) {
    lang = lang || getCurrentLang();
    const date = new Date();
    date.setFullYear(date.getFullYear() + 10);
    const domain = getModuleData('siteHost');
    document.cookie = '_lang=' + lang + ';expires=' + date.toUTCString() + ';domain=' + domain + ';path=/;';

    const doCallback = () => {
        if (callback) {
            callback();
        }
    };
    if (!!(getModuleData('userInfo') || {}).umu_id && needSave) {
        fetch
            .post('/api/user/updatesetup', {
                option_type: 7,
                option_value: lang,
            })
            .then(() => {
                doCallback();
            });
    } else {
        doCallback();
    }
}

// -----------------------------------

export function storageCurrentUserId(id: string) {
    // storageHelper.set('LOGIN_USER_ID', id || '0');
    localStorageHelper.set(STORAGE_KEY.LOGIN_USER_ID, id || '0');
}

export function getUserInfo() {
    return getModuleData('userInfo');
}

/**
 * 是否为企业版用户
 * @param {object} userInfo
 */
export function isEnterpriseUser() {
    return !!getData(getUserInfo(), 'enterprise_info', 'enterprise_id');
}

/**
 * 是否为企业版用户
 * @param {object} userInfo
 */
export function isEnterpriseAccount(userInfo: IUserInfo) {
    return !!getData(userInfo, 'enterprise_info', 'enterprise_id');
}

/**
 * 是否为注册用户
 * @param {object} userInfo
 */
export function isRegisterUser() {
    return !!getData(getUserInfo(), 'umu_id');
}

/**
 * 获取登录页面地址
 */
export function getLoginUrl(options: { isSkipProfile?: boolean } = {}) {
    let url = '/globaluser/loginpage?from_url=' + encodeURIComponent($window.location.href);
    if (options.isSkipProfile) {
        url += '#/skip_profile';
    }
    return url;
}

// reCaptcha 的site key
export const RECAPTCHA_SITE_KEY = '6LcusCsaAAAAAI2bbBt7Zif88KWZSeFqqSPQAEw1';
